import * as Sardine from '@sardine-ai/react-js-wrapper'
import { createContext, type PropsWithChildren, useContext, useEffect, useMemo } from 'react'
import type { UseSardine } from '@/src/services/Sardine'
import { useUserStore } from '@/src/common/store/useUserStore'
import { useSessionKey } from '@/src/common/hooks/useSessionKey'
import { useEvent } from 'tamagui'

export const SardineProvider = ((props) => (
  // @ts-expect-error King: will update once I hear back from Sardine team on which version is stable and
  // supports our version of RN / Expo
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  <Sardine.SardineProvider
    clientId={process.env.EXPO_PUBLIC_SARDINE_CLIENT_ID ?? ''}
    environment={process.env.EXPO_PUBLIC_ENVIRONMENT === 'production' ? 'production' : 'sandbox'}
    onSuccess={(...args) => console.log('Initialized Sardine on web', ...args)}
    apiSubdomain={process.env.SARDINE_API_SUBDOMAIN}
    pixelSubdomain={process.env.SARDINE_PIXEL_SUBDOMAIN}
  >
    <SetSardineSessionKeyAndUserIdHash>
      <MesaSardineProvider {...props} />
    </SetSardineSessionKeyAndUserIdHash>
    {/* eslint-disable-next-line @typescript-eslint/no-deprecated */}
  </Sardine.SardineProvider>
)) satisfies typeof import('@/src/services/Sardine').SardineProvider

/** the sardine sdk doesn't avoid re-renders very well. using this getter helps with this  */
function useGetSardineContext() {
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const context = useContext(Sardine.SardineContext)
  return useEvent(() => context)
}

const useSardineContext = (() => {
  const getContext = useGetSardineContext()
  return useMemo(
    () =>
      ({
        submitData() {},
        trackPage(_pageName) {},
        trackTextChange(_fieldName, _value) {},
        trackFocusChange(_fieldName, _hasFocus) {},
        updateOptions: (config) => {
          getContext().updateSardineConfig(config)
        },
      }) as const,
    [getContext],
  )
}) satisfies UseSardine

const SardineContext = createContext<ReturnType<typeof useSardineContext> | null>(null)

function MesaSardineProvider(props: PropsWithChildren) {
  return <SardineContext.Provider {...props} value={useSardineContext()} />
}

function SetSardineSessionKeyAndUserIdHash(props: PropsWithChildren) {
  const getContext = useGetSardineContext()

  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const userIdHash = useUserStore().userInformation?.id || ''
  const sessionKey = useSessionKey().sessionKey

  useEffect(() => {
    getContext().updateSardineConfig({
      sessionKey,
      customerId: userIdHash,
      enableBiometrics: true,
      enablePortScanning: true,
      flow: 'initialization',
    })
  }, [getContext, userIdHash, sessionKey])

  return props.children
}

export const useSardine = (() => {
  const context = useContext(SardineContext)
  if (context) return context
  throw new Error('useSardine must be used within a SardineProvider')
}) satisfies UseSardine
